import { Component, ChangeDetectionStrategy, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { IconsModule } from '@progress/kendo-angular-icons';
import { SVGIcon, userIcon, gearIcon } from '@progress/kendo-svg-icons';
import { IdentityRiskSummary } from '../../../../services/identity-security.service';

@Component({
  selector: 'app-xdr-correlation',
  standalone: true,
  imports: [CommonModule, DialogsModule, IconsModule],
  templateUrl: './xdr-correlation.component.html',
  styleUrl: './xdr-correlation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XdrCorrelationComponent {
  identityRisks = input<IdentityRiskSummary[]>([]);
  isLoading = input<boolean>(true);
  /**
   * The data could not be read, as opposed to there being none.
   *
   * An empty list and a failed request both arrive here as zero rows, and they mean
   * opposite things: one is "no identity was escalated", the other is "we did not
   * look". Claiming the first when the second happened is the same defect the posture
   * cards had.
   */
  unavailable = input<boolean>(false);

  protected selectedRisk = signal<IdentityRiskSummary | null>(null);
  protected dialogVisible = signal<boolean>(false);

  public onOpenDetails(risk: IdentityRiskSummary): void {
    this.selectedRisk.set(risk);
    this.dialogVisible.set(true);
  }

  public onDialogClose(): void {
    this.dialogVisible.set(false);
    this.selectedRisk.set(null);
  }

  public getIdentityTypeIcon(type: string): SVGIcon {
    return type === 'Service Account' ? gearIcon : userIcon;
  }

  public showsAppId(risk: IdentityRiskSummary): boolean {
    return (
      risk.identity_type === 'Service Account' &&
      this.identityRisks().filter((other) => other.identity === risk.identity).length > 1
    );
  }

  /** Shared severity chip classes (same definition used on the posture tab). */
  public getSeverityClass(severity: string): string {
    const tier = ['Critical', 'High', 'Medium', 'Low'].includes(severity) ? severity : 'Low';
    return `severity-badge severity-${tier.toLowerCase()}`;
  }

  public getMaxSeverity(risk: IdentityRiskSummary): string {
    const order = ['Critical', 'High', 'Medium', 'Low'];
    for (const level of order) {
      if (risk.misconfigs.some((m) => m.severity === level)) return level;
    }
    return 'Low';
  }
}
